import React, { useState, useEffect } from 'react';
import {
  Box, Heading, VStack, HStack, Stack, Input, Select, Button, IconButton, FormControl, FormLabel, useToast, Tag, Flex, Divider, Text, Switch, Icon,
  Menu, MenuButton, MenuList, MenuItem, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, useDisclosure,
  AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { FaEllipsisV } from 'react-icons/fa';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';

const tipos = [
  { label: 'Essencial', value: 'essencial', color: 'teal' },
  { label: 'Supérfluo', value: 'superfluo', color: 'orange' },
  { label: 'Investimento', value: 'investimento', color: 'green' },
];

const tiposLancamento = [
  { label: 'Despesa', value: 'despesa' },
  { label: 'Receita', value: 'receita' },
];

const Categorias: React.FC = () => {
  const { user } = useAuth();
  const [categorias, setCategorias] = useState<any[]>([]);
  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState('essencial');
  const [tipoLancamento, setTipoLancamento] = useState('despesa');
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editNome, setEditNome] = useState('');
  const [editTipo, setEditTipo] = useState('essencial');
  const [editTipoLancamento, setEditTipoLancamento] = useState('despesa');
  const [mostrarInativas, setMostrarInativas] = useState(false);
  const toast = useToast();
  const nova = useDisclosure();
  const [excluir, setExcluir] = useState<any>(null);
  const cancelExcluirRef = React.useRef<any>(null);

  useEffect(() => {
    if (!user) return;
    const fetchCategorias = async () => {
      setLoading(true);
      try {
        setCategorias(await api.get('/api/categorias'));
      } catch {
        setCategorias([]);
      }
      setLoading(false);
    };
    fetchCategorias();
  }, [user]);

  const handleAdd = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!nome.trim()) return;
    setLoading(true);
    try {
      await api.post('/api/categorias', { nome, tipo_lancamento: tipoLancamento, tipo });
      setNome('');
      setTipo('essencial');
      setTipoLancamento('despesa');
      nova.onClose();
      toast({ title: 'Categoria adicionada!', status: 'success' });
      setCategorias(await api.get('/api/categorias'));
    } catch {
      toast({ title: 'Erro ao adicionar categoria', status: 'error' });
    }
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      await api.delete(`/api/categorias/${id}`);
      setCategorias(categorias.filter(cat => cat.id !== id));
      toast({ title: 'Categoria excluída!', status: 'success' });
    } catch (err) {
      toast({
        title: 'Erro ao excluir categoria',
        description: err instanceof Error ? err.message : undefined,
        status: 'error',
      });
    }
    setLoading(false);
  };

  const startEdit = (cat: any) => {
    setEditId(cat.id);
    setEditNome(cat.nome);
    setEditTipo(cat.tipo);
    setEditTipoLancamento(cat.tipo_lancamento);
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditNome('');
    setEditTipo('essencial');
    setEditTipoLancamento('despesa');
  };

  const handleEdit = async (id: string) => {
    if (!editNome.trim()) return;
    setLoading(true);
    try {
      await api.put(`/api/categorias/${id}`, {
        nome: editNome,
        tipo_lancamento: editTipoLancamento,
        tipo: editTipo,
      });
      setEditId(null);
      setEditNome('');
      setEditTipo('essencial');
      setEditTipoLancamento('despesa');
      toast({ title: 'Categoria atualizada!', status: 'success' });
      setCategorias(await api.get('/api/categorias'));
    } catch {
      toast({ title: 'Erro ao editar categoria', status: 'error' });
    }
    setLoading(false);
  };

  const toggleAtivo = async (categoria: any) => {
    setLoading(true);
    try {
      await api.put(`/api/categorias/${categoria.id}`, { ativo: !categoria.ativo });
      setCategorias(categorias.map(c =>
        c.id === categoria.id ? { ...c, ativo: !c.ativo } : c
      ));
      toast({
        title: `Categoria ${!categoria.ativo ? 'ativada' : 'desativada'}!`,
        status: 'success'
      });
    } catch {
      toast({ title: 'Erro ao atualizar', status: 'error' });
    }
    setLoading(false);
  };

  const categoriasFiltradas = (mostrarInativas ? categorias : categorias.filter(c => c.ativo))
    .slice()
    .sort((a, b) => String(a.nome).localeCompare(String(b.nome), 'pt-BR'));

  // Grupos na ordem em que o dinheiro é pensado: o que entra, depois o 50/30/20
  const grupos = [
    { titulo: 'Receitas', cor: 'purple.400', itens: categoriasFiltradas.filter(c => c.tipo_lancamento === 'receita') },
    { titulo: 'Essenciais', dica: 'até 50% da receita', cor: 'teal.400', itens: categoriasFiltradas.filter(c => c.tipo_lancamento === 'despesa' && c.tipo === 'essencial') },
    { titulo: 'Supérfluos', dica: 'até 30% da receita', cor: 'orange.400', itens: categoriasFiltradas.filter(c => c.tipo_lancamento === 'despesa' && c.tipo === 'superfluo') },
    { titulo: 'Investimento', dica: 'pelo menos 20%', cor: 'green.400', itens: categoriasFiltradas.filter(c => c.tipo_lancamento === 'despesa' && c.tipo === 'investimento') },
    { titulo: 'Despesas sem tipo', dica: 'defina o tipo para contar no 50/30/20', cor: 'gray.400', itens: categoriasFiltradas.filter(c => c.tipo_lancamento === 'despesa' && !['essencial', 'superfluo', 'investimento'].includes(c.tipo)) },
  ].filter(g => g.itens.length);

  // O mesmo formulário serve para criar e editar
  const editando = !!editId;
  const campos = editando
    ? { nome: editNome, setNome: setEditNome, lanc: editTipoLancamento, setLanc: setEditTipoLancamento, tipo: editTipo, setTipo: setEditTipo }
    : { nome, setNome, lanc: tipoLancamento, setLanc: setTipoLancamento, tipo, setTipo };
  const fecharFormulario = () => {
    nova.onClose();
    cancelEdit();
  };

  return (
    <Box maxW="640px" mx="auto" p={4} pt={{ base: 4, md: 8 }}>
      <Flex justify="space-between" align="center" mb={3} gap={2}>
        <Heading size="lg">Categorias</Heading>
        <Button colorScheme="teal" size="sm" leftIcon={<AddIcon />} onClick={() => { cancelEdit(); nova.onOpen(); }}>Nova categoria</Button>
      </Flex>
      <Flex justify="flex-end" mb={3}>
        <HStack>
          <Text fontSize="sm" color="gray.600">Mostrar inativas</Text>
          <Switch isChecked={mostrarInativas} onChange={(e) => setMostrarInativas(e.target.checked)} />
        </HStack>
      </Flex>

      <VStack align="stretch" spacing={4}>
        {grupos.map(g => (
          <Box key={g.titulo} borderWidth="1px" borderColor="gray.100" borderRadius="xl" boxShadow="sm" overflow="hidden">
            <Flex align="center" gap={2} px={4} py={2} bg="gray.50" borderBottomWidth="1px" borderColor="gray.100">
              <Box w="10px" h="10px" borderRadius="full" bg={g.cor} />
              <Text fontWeight="semibold" fontSize="sm">{g.titulo}</Text>
              <Text fontSize="xs" color="gray.500">{g.itens.length}{g.dica ? ` · ${g.dica}` : ''}</Text>
            </Flex>
            <VStack align="stretch" spacing={0} divider={<Divider borderColor="gray.100" />}>
              {g.itens.map(categoria => (
                <Flex key={categoria.id} align="center" justify="space-between" px={4} py={2} gap={2} opacity={categoria.ativo ? 1 : 0.6}>
                  <Flex align="center" gap={2} minW={0}>
                    <Text fontSize="sm" noOfLines={1}>{categoria.nome}</Text>
                    {!categoria.ativo && <Tag size="sm" colorScheme="gray">inativa</Tag>}
                  </Flex>
                  <Menu placement="bottom-end">
                    <MenuButton as={IconButton} aria-label={`Ações de ${categoria.nome}`} icon={<Icon as={FaEllipsisV as any} />} variant="ghost" size="sm" />
                    <MenuList>
                      <MenuItem onClick={() => startEdit(categoria)}>Editar</MenuItem>
                      <MenuItem onClick={() => toggleAtivo(categoria)}>{categoria.ativo ? 'Desativar' : 'Reativar'}</MenuItem>
                      <MenuItem color="red.500" onClick={() => setExcluir(categoria)}>Excluir</MenuItem>
                    </MenuList>
                  </Menu>
                </Flex>
              ))}
            </VStack>
          </Box>
        ))}
        {!loading && grupos.length === 0 && (
          <Text color="gray.500">Nenhuma categoria ainda. Use "Nova categoria" para criar.</Text>
        )}
      </VStack>

      {/* Criar/editar */}
      <Modal isOpen={nova.isOpen || editando} onClose={fecharFormulario} isCentered>
        <ModalOverlay />
        <ModalContent mx={4} as="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); if (editando && editId) handleEdit(editId); else handleAdd(e); }}>
          <ModalHeader>{editando ? 'Editar categoria' : 'Nova categoria'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Nome</FormLabel>
                <Input value={campos.nome} onChange={e => campos.setNome(e.target.value)} placeholder="Ex: Moradia" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>É de</FormLabel>
                <Select value={campos.lanc} onChange={e => campos.setLanc(e.target.value)}>
                  {tiposLancamento.map(t => <option value={t.value} key={t.value}>{t.label}</option>)}
                </Select>
              </FormControl>
              {campos.lanc === 'despesa' && (
                <FormControl isRequired>
                  <FormLabel>Tipo no 50/30/20</FormLabel>
                  <Select value={campos.tipo || 'essencial'} onChange={e => campos.setTipo(e.target.value)}>
                    {tipos.map(t => <option value={t.value} key={t.value}>{t.label}</option>)}
                  </Select>
                </FormControl>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={fecharFormulario}>Cancelar</Button>
            <Button type="submit" colorScheme="teal" isLoading={loading}>{editando ? 'Salvar' : 'Adicionar'}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Confirmação de exclusão */}
      <AlertDialog isOpen={!!excluir} leastDestructiveRef={cancelExcluirRef} onClose={() => setExcluir(null)}>
        <AlertDialogOverlay>
          <AlertDialogContent mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Excluir {excluir?.nome}?</AlertDialogHeader>
            <AlertDialogBody>
              Categorias com lançamentos não podem ser excluídas; nesse caso, desative.
              Desativada, ela some das opções mas o histórico continua.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelExcluirRef} onClick={() => setExcluir(null)}>Cancelar</Button>
              <Button colorScheme="red" ml={3} isLoading={loading} onClick={async () => { if (excluir) await handleDelete(excluir.id); setExcluir(null); }}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default Categorias; 